import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { btn, StatusBadge, Modal, input, Field } from "@/components/kit";
import ProgressRing from "@/components/ProgressRing";
import { toast } from "sonner";
import {
  ArrowLeft, Link2, Trash2, CheckCircle2, RotateCcw, Upload, FileText, PenLine, CheckSquare, Info, ExternalLink, Mail, CreditCard, Loader2,
} from "lucide-react";

const TYPE_ICON = { document: Upload, form: FileText, signature: PenLine, checkbox: CheckSquare, info: Info, payment: CreditCard };
const TASK_LABEL = { pending: "Pending", submitted: "Waiting approval", completed: "Approved" };
const TASK_CLS = { pending: "bg-secondary text-foreground", submitted: "bg-amber-500/15 text-amber-600", completed: "bg-emerald-500/15 text-emerald-600" };
const isImage = (p) => /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(p || "");

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [action, setAction] = useState(null); // 'approve' | 'reject' | null
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveTpl, setSaveTpl] = useState(false);

  useEffect(() => {
    if (action) {
      setSaveTpl(false);
      api.get("/orgs/me/note-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
    }
  }, [action]);

  const openDoc = (path) => { if (isImage(path)) setLightbox(fileUrl(path)); else window.open(fileUrl(path), "_blank"); };

  const load = useCallback(() => api.get(`/clients/${id}`).then((r) => setC(r.data)).catch((e) => { toast.error(apiError(e)); nav("/app/clients"); }), [id, nav]);
  useEffect(() => { load(); }, [load]);

  const patch = async (taskId, body) => {
    try { const { data } = await api.patch(`/clients/${id}/tasks/${taskId}`, body); setC(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/onboard/${c.invite_token}`;
    navigator.clipboard?.writeText(url).then(() => toast.success("Invite link copied")).catch(() => toast.message(url));
  };

  const del = async () => {
    if (!window.confirm("Delete this client and all their onboarding data?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Client deleted"); nav("/app/clients"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remind = async () => {
    try { await api.post(`/clients/${id}/remind`); toast.success("Reminder email sent"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitAction = async () => {
    setActing(true);
    try {
      if (saveTpl && note.trim()) {
        try { await api.post("/orgs/me/note-templates", { kind: action, text: note.trim() }); } catch { /* non-blocking */ }
      }
      if (action === "approve") {
        const { data } = await api.patch(`/clients/${id}/approve`, { note });
        setC(data); toast.success("Signup approved");
      } else {
        await api.patch(`/clients/${id}/reject`, { note });
        toast.success("Signup declined"); load();
      }
      setAction(null); setNote("");
    } catch (e) { toast.error(apiError(e)); } finally { setActing(false); }
  };

  if (!c) return <div className="animate-pulse text-muted-foreground overline">Loading…</div>;
  const stages = [...new Set(c.tasks.map((t) => t.stage_name))];

  return (
    <div className="max-w-3xl">
      <button onClick={() => nav("/app/clients")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6"><ArrowLeft className="h-4 w-4" /> Back to clients</button>

      <div className="bg-card border border-border p-6 flex items-center justify-between gap-6 mb-8" data-testid="client-detail-header">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1"><h1 className="text-2xl font-bold truncate">{c.name}</h1><StatusBadge status={c.status} /></div>
          <p className="text-muted-foreground text-sm">{c.company && `${c.company} · `}{c.email}</p>
          <p className="text-xs text-muted-foreground mt-1">Flow: {c.flow_name}{c.source === "self_serve" && " · self-started from hub"}</p>
          {c.status === "pending_approval" && (
            <div className="flex items-center gap-2 mt-4" data-testid="signup-approval-actions">
              <button onClick={() => { setAction("approve"); setNote(""); }} className={btn.primary} data-testid="client-approve"><CheckCircle2 className="h-4 w-4" /> Approve signup</button>
              <button onClick={() => { setAction("reject"); setNote(""); }} className={btn.danger} data-testid="client-reject">Decline</button>
            </div>
          )}
          {c.admin_note && (
            <div className="mt-4 border border-border bg-secondary/50 px-3 py-2 text-sm" data-testid="client-admin-note">
              <span className="font-medium">{c.status === "declined" ? "Decline reason" : "Note to client"}:</span> {c.admin_note}
            </div>
          )}
          <div className="flex items-center gap-2 mt-4">
            <button onClick={copyLink} className={btn.outline} data-testid="client-copy-link"><Link2 className="h-4 w-4" /> Copy invite link</button>
            <a href={`/onboard/${c.invite_token}`} target="_blank" rel="noreferrer" className={btn.ghost} data-testid="client-open-portal"><ExternalLink className="h-4 w-4" /> Open portal</a>
            <button onClick={remind} className={btn.ghost} data-testid="client-remind"><Mail className="h-4 w-4" /> Send reminder</button>
            <button onClick={del} className={btn.ghost + " text-destructive"} data-testid="client-delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
        <ProgressRing value={c.progress} size={88} stroke={8} />
      </div>

      <div className="space-y-8">
        {stages.map((stage) => (
          <div key={stage}>
            <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-3">{stage}</h2>
            <div className="space-y-3">
              {c.tasks.filter((t) => t.stage_name === stage).map((t) => {
                const Icon = TYPE_ICON[t.type] || Info;
                return (
                  <div key={t.task_id} className="bg-card border border-border p-4" data-testid={`task-${t.task_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <span className="grid place-items-center h-8 w-8 bg-secondary shrink-0"><Icon className="h-4 w-4" /></span>
                        <div className="min-w-0">
                          <p className="font-medium">{t.title}{t.required && <span className="text-accent ml-1">*</span>}</p>
                          {t.value && <p className="text-sm text-muted-foreground mt-0.5">Response: <span className="text-foreground">{t.value}</span></p>}
                          {t.signature && (
                            <p className="text-xs text-muted-foreground mt-0.5" data-testid={`task-signature-${t.task_id}`}>
                              Signed by <span className="text-foreground font-medium">{t.signature.name}</span> · {new Date(t.signature.signed_at).toLocaleString()} · IP {t.signature.ip}
                            </p>
                          )}
                          {t.doc_path && <button onClick={() => openDoc(t.doc_path)} className="text-sm text-accent hover:underline mt-0.5 inline-block" data-testid={`view-doc-${t.task_id}`}>View uploaded file</button>}
                          {t.doc_paths?.length > 0 && <span className="flex flex-wrap gap-3 mt-0.5">{t.doc_paths.map((p, i) => <button key={i} onClick={() => openDoc(p)} className="text-sm text-accent hover:underline" data-testid={`view-doc-${t.task_id}-${i}`}>File {i + 1}</button>)}</span>}
                        </div>
                      </div>
                      <span className={`inline-flex items-center text-xs px-2.5 py-1 font-medium ${TASK_CLS[t.state] || "bg-secondary"}`} data-testid={`task-state-${t.task_id}`}>{TASK_LABEL[t.state] || t.state}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pl-11">
                      {t.state !== "completed" && (
                        <button onClick={() => patch(t.task_id, { state: "completed" })} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground font-medium inline-flex items-center gap-1 hover:bg-accent transition-colors" data-testid={`task-verify-${t.task_id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> {t.state === "submitted" ? "Approve" : "Approve"}
                        </button>
                      )}
                      {t.state !== "pending" && (
                        <button onClick={() => patch(t.task_id, { state: "pending" })} className="text-xs px-3 py-1.5 border border-border font-medium inline-flex items-center gap-1 hover:bg-secondary transition-colors" data-testid={`task-return-${t.task_id}`}>
                          <RotateCcw className="h-3.5 w-3.5" /> Return
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 grid place-items-center p-6 bg-foreground/80" onClick={() => setLightbox(null)} data-testid="lightbox">
          <img src={lightbox} alt="Uploaded document" className="max-h-[90vh] max-w-[90vw] object-contain border border-border" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white text-sm font-medium" data-testid="lightbox-close">Close ✕</button>
        </div>
      )}

      <Modal open={!!action} onClose={() => { setAction(null); setNote(""); }} title={action === "approve" ? "Approve signup" : "Decline signup"} testid="approval-modal">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {action === "approve"
              ? `Approve ${c.name}? You can add an optional welcome note they'll see in their portal.`
              : `Decline ${c.name}? Add an optional reason so they know why — it'll show on their portal and be emailed to them.`}
          </p>
          <Field label={action === "approve" ? "Note to client (optional)" : "Reason (optional)"}>
            <textarea rows={3} className={input} value={note} onChange={(e) => setNote(e.target.value)} data-testid="approval-note-input"
              placeholder={action === "approve" ? "Welcome aboard — let's get started!" : "e.g. We couldn't verify your details."} />
          </Field>
          {templates.filter((t) => t.kind === action).length > 0 && (
            <div>
              <label className="overline text-muted-foreground">Insert a saved template</label>
              <select className={input + " mt-1"} value="" onChange={(e) => { if (e.target.value) setNote(e.target.value); }} data-testid="approval-template-select">
                <option value="">Choose a saved note…</option>
                {templates.filter((t) => t.kind === action).map((t) => (
                  <option key={t.id} value={t.text}>{t.text.length > 60 ? t.text.slice(0, 60) + "…" : t.text}</option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={saveTpl} onChange={(e) => setSaveTpl(e.target.checked)} data-testid="approval-save-template" />
            Save this note as a reusable template
          </label>
          <div className="flex items-center gap-2">
            <button onClick={submitAction} disabled={acting} className={action === "approve" ? btn.primary : btn.danger} data-testid="approval-confirm">
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {action === "approve" ? "Approve signup" : "Decline signup"}
            </button>
            <button onClick={() => { setAction(null); setNote(""); }} className={btn.ghost} data-testid="approval-cancel">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
